/**
 * Connection debugging utilities
 *
 * Provides visibility into connection management for troubleshooting
 * the canceled/pending request issues.
 */

import { connectionManager } from './connectionManager'

// Debug logging flag - can be enabled in development
const DEBUG_CONNECTIONS = process.env.NODE_ENV === 'development'

export class ConnectionDebugger {
  private static logBuffer: string[] = []
  private static maxLogSize = 100

  static log(message: string) {
    if (!DEBUG_CONNECTIONS) return

    const timestamp = new Date().toISOString().slice(11, 23) // HH:MM:SS.mmm
    const logMessage = `[${timestamp}] ${message}`

    this.logBuffer.push(logMessage)
    if (this.logBuffer.length > this.maxLogSize) {
      this.logBuffer.shift()
    }
  }

  static getStatus() {
    const status = connectionManager.getStatus()
    const totalQueued = status.queued.high + status.queued.normal + status.queued.low

    return {
      active: status.active,
      maxConcurrent: status.maxConcurrent,
      queued: status.queued,
      totalQueued,
      utilizationPercent: Math.round((status.active / status.maxConcurrent) * 100),
      logs: this.logBuffer
    }
  }

  static logRequest(url: string, method: string, priority: string) {
    const status = connectionManager.getStatus()
    const shortUrl = url.length > 50 ? '...' + url.slice(-47) : url
    this.log(
      `${method} ${shortUrl} [${priority}] (Active: ${status.active}/${status.maxConcurrent})`
    )
  }

  static logRequestComplete(url: string, method: string, duration: number, success: boolean) {
    const shortUrl = url.length > 50 ? '...' + url.slice(-47) : url
    const status = success ? '✅' : '❌'
    this.log(`${status} ${method} ${shortUrl} (${duration}ms)`)
  }

  static logQueueStatus() {
    const status = connectionManager.getStatus()
    const totalQueued = status.queued.high + status.queued.normal + status.queued.low
    if (totalQueued > 0) {
      this.log(
        `Queue: ${status.queued.high}H + ${status.queued.normal}N + ${status.queued.low}L = ${totalQueued} waiting`
      )
    }
  }

  // Expose to window for console debugging
  static exposeToWindow() {
    if (typeof window !== 'undefined') {
      ;(window as unknown as Record<string, unknown>).connectionDebug = {
        getStatus: () => this.getStatus(),
        logStatus: () => console.table(this.getStatus()),
        clearLogs: () => {
          this.logBuffer = []
        },
        help: () => {
          console.log(`
🔗 Connection Debug Commands (Server-Only Cleanup):
• connectionDebug.getStatus() - Get connection pool status
• connectionDebug.logStatus() - Show status table  
• connectionDebug.clearLogs() - Clear debug logs
• connectionDebug.help() - Show this help

Note: Server handles all connection cleanup via Connection: close headers.
          `)
        }
      }
    }
  }
}

// Initialize debugging
ConnectionDebugger.exposeToWindow()

export const debugConnection = ConnectionDebugger
